/**
 * Lightweight fetch wrapper for the admin UI.
 *
 * Attaches `status` and `code` to thrown errors so TanStack Query's retry
 * logic (queryClient.ts) can skip retries on 4xx. The response body shape
 * matches the bridge's jsonError contract: { error: { code, message,
 * requestId, fieldErrors? } }.
 */

import type { ErrorResponse } from '@eotir/oidcbridge-shared/api-contracts';

export class ApiError extends Error {
  status: number;
  code: string;
  requestId?: string;
  fieldErrors?: Record<string, string>;

  constructor(status: number, body: ErrorResponse | null, fallback?: string) {
    const err = body?.error;
    super(err?.message ?? fallback ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = err?.code ?? 'system.unexpected';
    this.requestId = err?.requestId;
    this.fieldErrors = err?.fieldErrors;
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'same-origin',
    signal: options.signal,
    headers: { 'Content-Type': 'application/json' },
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }

  const res = await fetch(path, init);
  if (!res.ok) {
    let errBody: ErrorResponse | null = null;
    try {
      errBody = (await res.json()) as ErrorResponse;
    } catch {
      // Non-JSON body — leave errBody null
    }
    throw new ApiError(res.status, errBody);
  }
  // DELETE / 204-style responses may have empty bodies.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
